import { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

const AppInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user has dismissed the banner
    const dismissed = localStorage.getItem('app-banner-dismissed');
    
    // Show banner if not installed and not dismissed recently (within 3 days)
    if (!standalone) {
      if (!dismissed) {
        setShowBanner(true);
      } else {
        const dismissedTime = parseInt(dismissed);
        const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed > 3) {
          setShowBanner(true);
        }
      }
    }

    // For non-iOS, listen for beforeinstallprompt
    if (!iOS) {
      const handler = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('app-banner-dismissed', Date.now().toString());
    setShowBanner(false);
    setShowInstructions(false);
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // For Android/Desktop - trigger native install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA install outcome:', outcome);
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        handleDismiss();
      }
    } else {
      // Show instructions modal
      setShowInstructions(true);
    }
  };

  // Don't show if already installed
  if (isStandalone || !showBanner) return null;

  return (
    <>
      {/* Top Banner */}
      <div 
        className="w-full py-2 px-3 sm:px-4 flex items-center justify-center gap-2 sm:gap-3 flex-wrap"
        style={{ backgroundColor: '#D66238' }}
      >
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-white flex-shrink-0" />
          <span className="text-white text-xs sm:text-sm font-medium whitespace-nowrap">
            Get the Wild App!
          </span>
        </div>
        <button
          onClick={handleInstallClick}
          className="text-white text-xs sm:text-sm font-bold underline hover:no-underline flex items-center gap-1"
        >
          <Download className="w-3 h-3" />
          Install Now
        </button>
        <button
          onClick={handleDismiss}
          className="text-white/80 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
          <div 
            className="bg-white rounded-2xl shadow-2xl border-2 p-6 mx-4 max-w-md w-full animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto"
            style={{ borderColor: '#6FCFD3' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Install Wild Mustang Life</h2>
              <button onClick={() => setShowInstructions(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* App Icon */}
            <div className="text-center mb-4">
              <div 
                className="w-20 h-20 rounded-2xl mx-auto shadow-lg overflow-hidden"
                style={{ backgroundColor: '#6FCFD3' }}
              >
                <img 
                  src="/icons/icon-192.png" 
                  alt="Wild Mustang Life" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {isIOS ? (
              /* iOS Instructions */
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <strong>📱 iPhone/iPad Users:</strong> You must use Safari to install this app.
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Follow these steps:</h3>
                  
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      1
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Open in Safari</p>
                      <p className="text-sm text-gray-600">Make sure you're viewing this page in Safari (not Chrome or another browser)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      2
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        Tap the Share button
                        <Share className="w-5 h-5 text-blue-500" />
                      </p>
                      <p className="text-sm text-gray-600">It's at the bottom of the screen (square with arrow pointing up)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      3
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 flex items-center gap-2">
                        Tap "Add to Home Screen"
                        <Plus className="w-5 h-5 text-gray-700" />
                      </p>
                      <p className="text-sm text-gray-600">Scroll down in the share menu to find it</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      4
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Tap "Add"</p>
                      <p className="text-sm text-gray-600">The app will appear on your home screen!</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => setShowInstructions(false)}
                    className="w-full"
                    style={{ backgroundColor: '#6FCFD3' }}
                  >
                    Got it!
                  </Button>
                </div>
              </div>
            ) : (
              /* Android/Desktop Instructions */
              <div className="space-y-4">
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Follow these steps:</h3>
                  
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      1
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Look for the install prompt</p>
                      <p className="text-sm text-gray-600">Your browser should show an "Install" option in the address bar or menu</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      2
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">On Chrome (Android)</p>
                      <p className="text-sm text-gray-600">Tap the three dots menu (⋮) → "Add to Home screen" or "Install app"</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div 
                      className="flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold flex-shrink-0"
                      style={{ backgroundColor: '#6FCFD3' }}
                    >
                      3
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Confirm installation</p>
                      <p className="text-sm text-gray-600">The app will be added to your home screen and app drawer</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>💡 Tip:</strong> After installing, you can find the app in your app drawer or by searching "Wild Mustang"
                </div>

                <div className="pt-4 border-t">
                  <Button 
                    onClick={() => setShowInstructions(false)}
                    className="w-full"
                    style={{ backgroundColor: '#6FCFD3' }}
                  >
                    Got it!
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AppInstallBanner;
